import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { dateToMonDDYYYY,convertTo12HourFormat } from '../utils/dateUtils';
import { getValidEnrollment } from '../utils/enrollmentUtils';

const PLACEHOLDER_IMG = 'https://via.placeholder.com/200x200?text=Event';

function getTodayISO() {
  const today = new Date();
  return today.toISOString().slice(0, 10);
}

const FEeventRegistration = ({ userData }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ inpersondesired: false, needstransportation: false });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [modalError, setModalError] = useState('');
  const [jobsByEvent, setJobsByEvent] = useState({}); // { eventId: jobsCount }
  const [carouselStartIdx, setCarouselStartIdx] = useState(0);
  const [validEnrollment, setValidEnrollment] = useState(null);
  const [registeredEventIds, setRegisteredEventIds] = useState([]);

  useEffect(() => {
    const fetchEventsAndJobs = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        // Fetch all official events (live and locked)
        const res = await axios.get('/api/field-experience-events/by-stage/official', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter out hidden events
        let filtered = (res.data.events || []).filter(e => e.status !== 'hidden');
        // Sort by date ascending (oldest to newest)
        filtered = filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
        // Fetch jobs count for all events in a single batch call
        const jobsCounts = {};
        try {
          const eventIds = filtered.map(e => e.id).join(',');
          const jobsRes = await axios.get(`/api/field-experience-events/jobsbyevent/by-events?event_ids=${eventIds}&status=event`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const jobs = jobsRes.data.jobsbyevent || [];
          // Group jobs by event_id and sum slots
          jobs.forEach(job => {
            if (job.feevent_id) {
              jobsCounts[job.feevent_id] = (jobsCounts[job.feevent_id] || 0) + (job.slots || 0);
            }
          });
          // Ensure all events have a count (even if 0)
          filtered.forEach(event => {
            if (jobsCounts[event.id] === undefined) {
              jobsCounts[event.id] = 0;
            }
          });
        } catch (err) {
          console.warn('Failed to fetch job counts:', err);
          // Set all counts to 0 as fallback
          filtered.forEach(event => {
            jobsCounts[event.id] = 0;
          });
        }
        setJobsByEvent(jobsCounts);
        setEvents(filtered);
        // Find the next event after today
        const todayISO = getTodayISO();
        const now = new Date(todayISO);
        const nextIdx = filtered.findIndex(e => new Date(e.date) > now);
        let carouselStart = 0;
        let idx = 0;
        if (nextIdx !== -1) {
          // There is a future event
          idx = nextIdx;
          // Try to show nextIdx as the first in a window of 4
          if (filtered.length - nextIdx >= 4) {
            carouselStart = nextIdx;
          } else {
            // Not enough future events, back up to show 4 events if possible
            carouselStart = Math.max(0, filtered.length - 4);
          }
        } else {
          // No future events, show the last 4 events
          idx = filtered.length - 1;
          carouselStart = Math.max(0, filtered.length - 4);
        }
        setSelectedIdx(idx);
        setCarouselStartIdx(carouselStart);
      } catch (err) {
        setError('Failed to load events.');
      } finally {
        setLoading(false);
      }
    };
    fetchEventsAndJobs();
  }, []);

  useEffect(() => {
    setValidEnrollment(getValidEnrollment(userData));
  }, [userData]);

  // Fetch user's registrations for visible events after events and userData are loaded
  useEffect(() => {
    const fetchRegistrations = async () => {
      if (!userData || !events.length) return;
      try {
        const token = localStorage.getItem('token');
        // Get all event IDs
        const eventIds = events.map(e => e.id);
        // Fetch registrations for these events for the current user
        const res = await axios.get('/api/feeventregistrations/mine', {
          params: { feevent_ids: eventIds.join(',') },
          headers: { Authorization: `Bearer ${token}` }
        });
        // Assume the API returns an array of registrations with feevent_id
        setRegisteredEventIds(res.data.registrations.map(r => r.feevent_id));
      } catch (err) {
        setRegisteredEventIds([]); // fallback: no registrations
      }
    };
    fetchRegistrations();
  }, [userData, events]);

  const selectedEvent = events[selectedIdx] || null;

  const handleArrow = (dir) => {
    setCarouselStartIdx((prev) => {
      if (dir === 'left') return Math.max(0, prev - 4);
      if (dir === 'right') return Math.min(events.length - 4, prev + 4);
      return prev;
    });
    setSuccess('');
    setModalError('');
  };

  const handleCardClick = (idx) => {
    setSelectedIdx(idx);
    if (idx < carouselStartIdx || idx >= carouselStartIdx + 4) {
      setCarouselStartIdx(Math.max(0, idx - (idx % 4)));
    }
    setSuccess('');
    setModalError('');
  };

  const openModal = () => {
    // Set form defaults based on event type
    if (selectedEvent) {
      const type = selectedEvent.type;
      if (type === 'Virtual') {
        setForm({ inpersondesired: false, needstransportation: false });
      } else if (type === 'In-Person' || type === 'In Person') {
        setForm({ inpersondesired: true, needstransportation: false });
      } else if (type === 'Hybrid') {
        setForm({ inpersondesired: false, needstransportation: false });
      } else {
        setForm({ inpersondesired: false, needstransportation: false });
      }
    } else {
      setForm({ inpersondesired: false, needstransportation: false });
    }
    setModalOpen(true);
    setSuccess('');
    setModalError('');
  };
  const closeModal = () => {
    setModalOpen(false);
    setSuccess('');
    setModalError('');
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Helper to check if user is a student with a valid enrollment
  function canRegister(userData, validEnrollment) {
    if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
    const card = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
    if (!card || card.role !== 'Student') return false;
    return !!validEnrollment;
  }

  // Helper to check if user is a Student
  function isStudent(userData) {
    if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
    const card = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
    return card && card.role === 'Student';
  }

  const handleRegister = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setModalError('');
    setSuccess('');
    try {
      if (!validEnrollment) {
        setModalError('No valid enrollment found for registration.');
        setSubmitting(false);
        return;
      }
      const token = localStorage.getItem('token');
      // Check for duplicate registration (fresh from backend)
      const checkRes = await axios.get('/api/feeventregistrations/mine', {
        params: { feevent_ids: selectedEvent.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      const alreadyRegistered = (checkRes.data.registrations || []).some(
        r => r.feevent_id === selectedEvent.id && r.enrollment === validEnrollment.enrollment_id
      );
      if (alreadyRegistered) {
        setModalError('You are already registered for this event.');
        // Add the event to registeredEventIds so both buttons are disabled
        setRegisteredEventIds(prev => [...prev, selectedEvent.id]);
        setSubmitting(false);
        return;
      }
      const payload = {
        feevent_id: selectedEvent.id,
        inpersondesired: form.inpersondesired,
        needstransportation: form.needstransportation,
        enrollment: validEnrollment.enrollment_id,
      };
      await axios.post('/api/feeventregistrations/register', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Registration successful!');
      // Add the event to registeredEventIds so the button updates immediately
      setRegisteredEventIds(prev => [...prev, selectedEvent.id]);
      setModalOpen(false);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get the event label
  function getEventLabel(event, idx, events) {
    if (!event || !events.length) return '';
    const todayISO = getTodayISO();
    const todayIdx = events.findIndex(e => e.date === todayISO);
    const now = new Date(todayISO);
    // Find the next event after today
    const nextIdx = events.findIndex(e => new Date(e.date) > now);
    // Find the last event before today
    let lastIdx = -1;
    for (let i = 0; i < events.length; i++) {
      if (new Date(events[i].date) < now) lastIdx = i;
      else break;
    }
    if (event.date === todayISO) return "Today's Event";
    if (nextIdx !== -1 && idx === nextIdx) return 'Next Event';
    if (lastIdx !== -1 && idx === lastIdx) return 'Last Event';
    if (lastIdx !== -1 && idx < lastIdx) return 'Previous Event';
    if (nextIdx !== -1 && idx > nextIdx) return 'Upcoming Event';
    return '';
  }

  // Registration form logic based on event type
  const renderRegistrationForm = () => {
    if (!selectedEvent) return null;
    const type = selectedEvent.type;
    const isAlreadyRegistered = registeredEventIds.includes(selectedEvent.id);
    
    if (type === 'Virtual') {
      return (
        <form onSubmit={handleRegister} style={styles.formBox}>
          <input type="hidden" name="inpersondesired" value="false" />
          <input type="hidden" name="needstransportation" value="false" />
          <button type="submit" style={styles.submitButton} disabled={submitting || isAlreadyRegistered}>
            {submitting ? 'Signing up...' : isAlreadyRegistered ? 'Already Registered' : 'Sign up'}
          </button>
        </form>
      );
    }
    if (type === 'Hybrid') {
      return (
        <form onSubmit={handleRegister} style={styles.formBox}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Do you plan to come in person?</label>
            <div>
              <label className="radio-label" style={styles.radioLabel}>
                <input
                  type="radio"
                  name="inpersondesired"
                  checked={form.inpersondesired === true}
                  onChange={() => handleFormChange('inpersondesired', true)}
                  disabled={submitting || isAlreadyRegistered}
                />
                Yes
              </label>
              <label className="radio-label" style={styles.radioLabel}>
                <input
                  type="radio"
                  name="inpersondesired"
                  checked={form.inpersondesired === false}
                  onChange={() => handleFormChange('inpersondesired', false)}
                  disabled={submitting || isAlreadyRegistered}
                />
                No
              </label>
            </div>
          </div>
          {form.inpersondesired && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Do you need transportation?</label>
              <label className="radio-label" style={styles.radioLabel}>
                <input
                  type="radio"
                  name="needstransportation"
                  checked={form.needstransportation === true}
                  onChange={() => handleFormChange('needstransportation', true)}
                  disabled={submitting || isAlreadyRegistered}
                />
                Yes
              </label>
              <label className="radio-label" style={styles.radioLabel}>
                <input
                  type="radio"
                  name="needstransportation"
                  checked={form.needstransportation === false}
                  onChange={() => handleFormChange('needstransportation', false)}
                  disabled={submitting || isAlreadyRegistered}
                />
                No
              </label>
            </div>
          )}
          <button type="submit" style={styles.submitButton} disabled={submitting || isAlreadyRegistered}>
            {submitting ? 'Signing up...' : isAlreadyRegistered ? 'Already Registered' : 'Sign up'}
          </button>
        </form>
      );
    }
    if (type === 'In-Person' || type === 'In Person') {
      return (
        <form onSubmit={handleRegister} style={styles.formBox}>
          <input type="hidden" name="inpersondesired" value="true" />
          <div style={styles.formGroup}>
            <label style={styles.label}>Do you need transportation?</label>
            <label className="radio-label" style={styles.radioLabel}>
              <input
                type="radio"
                name="needstransportation"
                checked={form.needstransportation === true}
                onChange={() => handleFormChange('needstransportation', true)}
                disabled={submitting}
              />
              Yes
            </label>
            <label className="radio-label" style={styles.radioLabel}>
              <input
                type="radio"
                name="needstransportation"
                checked={form.needstransportation === false}
                onChange={() => handleFormChange('needstransportation', false)}
                disabled={submitting}
              />
              No
            </label>
          </div>
          <button type="submit" style={styles.submitButton} disabled={submitting}>{submitting ? 'Signing up...' : 'Sign up'}</button>
        </form>
      );
    }
    return <div style={styles.infoBox}>Unknown event type.</div>;
  };

  // In the render, only show up to 4 events in the carousel window
  const carouselEvents = events.slice(carouselStartIdx, carouselStartIdx + 4);

  if (!userData) {
    return <div style={styles.container}>Please log in to register for field experience events.</div>;
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="mobile-container" style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(30px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          .carousel-card:hover {
            transform: translateY(-3px) !important;
            box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
            border-color: #007bff !important;
          }
          .carousel-card:hover .carousel-image {
            transform: scale(1.02);
          }
          .arrow-button:hover:not(:disabled) {
            background: #e9ecef !important;
            border-color: #adb5bd !important;
            transform: scale(1.05) !important;
          }
          .signup-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(0, 123, 255, 0.3) !important;
          }
          .modal-close:hover {
            background: #e9ecef !important;
            border-color: #adb5bd !important;
            transform: scale(1.05);
          }
          .radio-label:hover {
            background-color: #f8f9fa !important;
          }
          .details-image:hover {
            transform: scale(1.01);
            box-shadow: 0 12px 30px rgba(0,0,0,0.15) !important;
          }
          
          /* Desktop Layout Enhancements */
          @media (min-width: 769px) {
            .desktop-image-column {
              width: 400px !important;
              min-width: 400px !important;
              flex-shrink: 0 !important;
            }
            .desktop-details-column {
              flex: 1 !important;
              padding: 2.5rem !important;
            }
            .desktop-image {
              width: 100% !important;
              height: auto !important;
              max-height: 600px !important;
              object-fit: cover !important;
            }
          }
          
          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .mobile-stack {
              flex-direction: column !important;
              gap: 1.5rem !important;
            }
            .mobile-full-width {
              width: 100% !important;
              min-width: auto !important;
            }
            .mobile-center {
              text-align: center !important;
            }
            .mobile-padding {
              padding: 1rem !important;
            }
            .mobile-small-image {
              width: 200px !important;
              height: 280px !important;
              border-radius: 8px !important;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
            }
            .mobile-small-title {
              font-size: 1.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            .mobile-small-text {
              font-size: 0.9rem !important;
            }
            .mobile-small-arrows {
              width: 35px !important;
              height: 35px !important;
              font-size: 1.2rem !important;
              padding: 0.5rem !important;
            }
            .mobile-scroll {
              overflow-x: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
            .mobile-modal {
              margin: 1rem !important;
              min-width: auto !important;
              width: calc(100vw - 2rem) !important;
            }
            .mobile-container {
              padding: 0.5rem !important;
            }
            .mobile-title {
              font-size: 1.8rem !important;
              margin-bottom: 1rem !important;
            }
            .mobile-details-panel {
              padding: 1rem !important;
              min-height: auto !important;
              background: #ffffff !important;
              border-radius: 12px !important;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
              border: 1px solid #e9ecef !important;
              margin: 0 0.5rem !important;
            }
            .mobile-carousel-container {
              padding: 0.75rem !important;
              margin: 0.5rem 0.5rem !important;
            }
            .mobile-carousel-card {
              min-width: 140px !important;
              max-width: 140px !important;
              padding: 0.75rem !important;
            }
            .mobile-carousel-image {
              width: 100px !important;
              height: 100px !important;
            }
            .mobile-details-row {
              margin-bottom: 0.2rem !important;
              padding: 0.2rem 0 !important;
            }
            .mobile-signup-margin {
              margin-top: 0.25rem !important;
            }
            .mobile-event-label {
              position: relative !important;
              top: auto !important;
              left: auto !important;
              right: auto !important;
              margin-bottom: 0.75rem !important;
              font-size: 0.85rem !important;
              text-align: center !important;
              background: rgba(255, 255, 255, 0.95) !important;
              padding: 0.4rem 0.8rem !important;
              border-radius: 6px !important;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
              border: 1px solid rgba(0,0,0,0.1) !important;
              display: inline-block !important;
              width: auto !important;
            }
          }
          
          @media (max-width: 480px) {
            .mobile-tiny-image {
              width: 150px !important;
              height: 210px !important;
              border-radius: 6px !important;
            }
            .mobile-tiny-title {
              font-size: 1.2rem !important;
              margin-bottom: 0.25rem !important;
            }
            .mobile-tiny-text {
              font-size: 0.85rem !important;
            }
            .mobile-tiny-padding {
              padding: 0.75rem !important;
            }
            .mobile-tiny-event-label {
              font-size: 0.75rem !important;
              padding: 0.3rem 0.6rem !important;
              margin-bottom: 0.25rem !important;
            }
            .mobile-tiny-details-panel {
              padding: 0.75rem !important;
            }
          }
        `}
      </style>
      <h1 className="mobile-title" style={styles.title}>Register for a Field Experience Event</h1>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}
      <div className="mobile-stack mobile-details-panel mobile-tiny-details-panel" style={styles.detailsPanel}>
        {selectedEvent ? (
          <>
            <div className="mobile-full-width mobile-center desktop-image-column" style={styles.detailsLeft}>
              {/* Event label above the cover photo */}
              <div className="mobile-small-title mobile-event-label mobile-tiny-event-label" style={styles.eventLabel}>{getEventLabel(selectedEvent, selectedIdx, events)}</div>
              <img
                src={selectedEvent.coverphoto || selectedEvent.coverPhoto || PLACEHOLDER_IMG}
                alt="Event"
                className="details-image mobile-small-image mobile-tiny-image desktop-image"
                style={{ ...styles.detailsImage, objectFit: 'cover' }}
              />
            </div>
            <div className="mobile-full-width desktop-details-column" style={styles.detailsRight}>
              <div className="mobile-small-title" style={styles.detailsTitle}>{selectedEvent.title || 'Untitled Event'}</div>
              <div className="mobile-small-text mobile-details-row" style={styles.detailsRow}>
                <strong>When:</strong> {convertTo12HourFormat(selectedEvent.start_time) || ''} to {convertTo12HourFormat(selectedEvent.end_time) || ''}{(selectedEvent.start_time || selectedEvent.end_time) ? '' : ''} on {selectedEvent.date ? dateToMonDDYYYY(selectedEvent.date, false) : ''}
              </div>
              <div className="mobile-small-text mobile-details-row" style={styles.detailsRow}><strong>Where:</strong> {selectedEvent.location || selectedEvent.where || 'TBA'}</div>
              <div className="mobile-small-text mobile-details-row" style={styles.detailsRow}>
                <strong>Type:</strong> {
                  selectedEvent.type === 'In-Person' || selectedEvent.type === 'In Person'
                    ? 'In Person'
                    : selectedEvent.type === 'Virtual'
                    ? 'Virtual'
                    : selectedEvent.type === 'Hybrid'
                    ? 'In Person + Virtual'
                    : selectedEvent.type || 'N/A'
                }
              </div>
              <div className="mobile-small-text mobile-details-row" style={styles.detailsRow}><strong>Hours:</strong> {selectedEvent.hoursavailable || 'N/A'}</div>
              <div className="mobile-small-text mobile-details-row" style={styles.detailsRow}><strong>Jobs:</strong> {jobsByEvent[selectedEvent.id] !== undefined ? `${jobsByEvent[selectedEvent.id]} job positions available` : 'N/A'}</div>
              <div className="mobile-signup-margin" style={{ marginTop: '0.5rem' }}>
                {selectedEvent.status === 'live' ? (
                  registeredEventIds.includes(selectedEvent.id) ? (
                    <button style={{ ...styles.signupButton, backgroundColor: '#bbb', cursor: 'not-allowed' }} disabled>Registered</button>
                  ) : canRegister(userData, validEnrollment) ? (
                    <button className="signup-button" style={styles.signupButton} onClick={openModal}>Sign up</button>
                  ) : !isStudent(userData) ? (
                    <button className="signup-button" style={{ ...styles.signupButton, backgroundColor: '#bbb', cursor: 'not-allowed' }} disabled title="Only students may sign up">Sign up (Only for Students)</button>
                  ) : (
                    <button className="signup-button" style={{ ...styles.signupButton, backgroundColor: '#bbb', cursor: 'not-allowed' }} disabled title="Only students with a valid enrollment may sign up">Sign up (Valid enrollment required)</button>
                  )
                ) : (
                  <button style={styles.lockedButton} disabled>Locked</button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div style={styles.infoBox}>No event selected.</div>
        )}
      </div>
      {/* Carousel */}
      <div className="mobile-padding mobile-carousel-container" style={styles.carouselContainer}>
        <button
          className="arrow-button mobile-small-arrows"
          style={styles.arrowButton}
          onClick={() => handleArrow('left')}
          disabled={carouselStartIdx === 0}
        >
          &#8592;
        </button>
        <div className="mobile-scroll" style={styles.carouselList}>
          {carouselEvents.map((event, idx) => {
            const globalIdx = carouselStartIdx + idx;
            return (
              <div
                key={event.id}
                className="carousel-card mobile-carousel-card"
                style={{
                  ...styles.carouselCard,
                  border: globalIdx === selectedIdx ? '2px solid #007bff' : '2px solid transparent',
                  boxShadow: globalIdx === selectedIdx ? '0 6px 18px rgba(0, 123, 255, 0.2)' : '0 4px 12px rgba(0,0,0,0.08)'
                }}
                onClick={() => handleCardClick(globalIdx)}
              >
                <div className="mobile-small-text" style={styles.carouselDate}>{event.date ? dateToMonDDYYYY(event.date, false) : 'No Date'}</div>
                <img
                  src={event.coverphoto || event.coverPhoto || PLACEHOLDER_IMG}
                  alt="Event"
                  className="carousel-image mobile-carousel-image"
                  style={{ ...styles.carouselImage, objectFit: 'contain' }}
                />
              </div>
            );
          })}
        </div>
        <button
          className="arrow-button mobile-small-arrows"
          style={styles.arrowButton}
          onClick={() => handleArrow('right')}
          disabled={carouselStartIdx + 4 >= events.length}
        >
          &#8594;
        </button>
      </div>
      {/* Modal Popup */}
      {modalOpen && (
        <div style={styles.modalOverlay}>
          <div className="mobile-modal" style={styles.modalContent}>
            <button className="modal-close" style={styles.modalClose} onClick={closeModal}>&times;</button>
            <h2 className="mobile-small-title" style={styles.modalTitle}>Sign up for {selectedEvent?.title || 'Event'}</h2>
            {/* Event type message */}
            {selectedEvent?.type === 'In-Person' || selectedEvent?.type === 'In Person' ? (
              <div style={styles.infoBox}>This is an In person event.</div>
            ) : selectedEvent?.type === 'Virtual' ? (
              <div style={styles.infoBox}>This is a virtual only event.</div>
            ) : selectedEvent?.type === 'Hybrid' ? (
              <div style={styles.infoBox}>This event is available in person or virtually.</div>
            ) : null}
            {modalError && <div style={styles.error}>{modalError}</div>}
            {renderRegistrationForm()}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    background: '#f8f9fa',
    minHeight: '100vh',
    borderRadius: '0',
    boxShadow: 'none'
  },
  title: {
    fontSize: '2.2rem',
    marginBottom: '2.5rem',
    color: '#2c3e50',
    textAlign: 'center',
    fontWeight: '600',
    letterSpacing: '-0.3px'
  },
  detailsPanel: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0',
    marginBottom: '3rem',
    background: '#ffffff',
    borderRadius: '12px',
    padding: '0',
    alignItems: 'stretch',
    minHeight: '500px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: '1px solid #e9ecef',
    overflow: 'hidden'
  },
  detailsLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '400px',
    minWidth: '400px',
    background: '#f8f9fa',
    padding: '2rem',
    justifyContent: 'center',
    position: 'relative'
  },
  detailsImage: {
    width: '100%',
    height: 'auto',
    maxHeight: '600px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: 'none',
    background: '#fff',
    boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
  },
  statusLive: {
    marginTop: '1rem',
    padding: '0.75rem 2rem',
    backgroundColor: '#28a745',
    color: 'white',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '1rem',
    border: 'none',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)'
  },
  statusLocked: {
    marginTop: '1rem',
    padding: '0.75rem 2rem',
    backgroundColor: '#6c757d',
    color: 'white',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '1rem',
    border: 'none',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(108, 117, 125, 0.2)'
  },
  detailsRight: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    fontSize: '1.1rem',
    color: '#495057',
    padding: '2.5rem',
    justifyContent: 'center'
  },
  detailsTitle: {
    fontSize: '1.8rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: '#2c3e50',
    lineHeight: '1.3'
  },
  detailsRow: {
    marginBottom: '0.4rem',
    padding: '0.3rem 0',
    borderBottom: '1px solid #f1f3f4',
    fontSize: '1rem',
    lineHeight: '1.6'
  },
  signupButton: {
    padding: '0.875rem 2rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '1rem',
    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.2)'
  },
  lockedButton: {
    padding: '0.875rem 2rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '500',
    marginTop: '1rem',
    cursor: 'not-allowed',
    boxShadow: '0 4px 12px rgba(108, 117, 125, 0.2)'
  },
  carouselContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    margin: '2.5rem 0 2rem 0',
    justifyContent: 'center',
    background: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: '1px solid #e9ecef'
  },
  arrowButton: {
    fontSize: '1.5rem',
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    color: '#495057',
    cursor: 'pointer',
    padding: '0.75rem',
    userSelect: 'none',
    transition: 'all 0.3s ease',
    borderRadius: '8px',
    width: '45px',
    height: '45px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  carouselList: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1.5rem',
    overflowX: 'auto',
    padding: '0.5rem 0',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  carouselCard: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '1rem',
    minWidth: '160px',
    maxWidth: '160px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    border: '2px solid transparent'
  },
  carouselDate: {
    fontWeight: '600',
    fontSize: '0.9rem',
    marginBottom: '0.75rem',
    color: '#495057',
    textAlign: 'center'
  },
  carouselImage: {
    width: '120px',
    height: '120px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: 'none',
    background: '#f8f9fa',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.5)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.3s ease'
  },
  modalContent: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '2rem',
    minWidth: '400px',
    maxWidth: '95vw',
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: '1px solid #e9ecef',
    animation: 'slideUp 0.3s ease'
  },
  modalClose: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    fontSize: '1.2rem',
    color: '#6c757d',
    cursor: 'pointer',
    borderRadius: '6px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: '1.5rem',
    color: '#2c3e50',
    textAlign: 'center'
  },
  formBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid #e9ecef',
    width: '100%'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  label: {
    fontWeight: '500',
    color: '#495057',
    marginBottom: '0.5rem',
    fontSize: '1rem'
  },
  radioLabel: {
    marginRight: '1.5rem',
    fontSize: '0.95rem',
    color: '#495057',
    fontWeight: '400',
    cursor: 'pointer',
    marginLeft: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    borderRadius: '6px',
    transition: 'background-color 0.2s ease'
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '1rem',
    boxShadow: '0 2px 8px rgba(0, 123, 255, 0.2)'
  },
  infoBox: {
    background: '#e3f2fd',
    border: '1px solid #bbdefb',
    borderRadius: '8px',
    padding: '1rem',
    color: '#1565c0',
    marginBottom: '1rem',
    fontSize: '0.95rem',
    lineHeight: '1.5'
  },
  error: {
    padding: '1rem',
    background: '#ffebee',
    color: '#c62828',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #ffcdd2',
    fontSize: '0.95rem',
    fontWeight: '400'
  },
  success: {
    padding: '1rem',
    background: '#e8f5e8',
    color: '#2e7d32',
    borderRadius: '8px',
    marginBottom: '1rem',
    border: '1px solid #c8e6c9',
    fontSize: '0.95rem',
    fontWeight: '400'
  },
  eventLabel: {
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#6c757d',
    textAlign: 'left',
    marginBottom: '0',
    letterSpacing: '0.5px',
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    background: 'rgba(255, 255, 255, 0.9)',
    padding: '0.4rem 0.8rem',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textTransform: 'uppercase',
    border: '1px solid rgba(0,0,0,0.05)'
  }
};

export default FEeventRegistration; 